import { Controller } from "@hotwired/stimulus"

// Loads a lazy Turbo Frame once it approaches view inside a scrolling ancestor.
//
// Turbo's own loading="lazy" observer watches the viewport, and in practice it
// does not re-fire for an element revealed by scrolling a horizontally
// overflowing ancestor — which is exactly how board columns are laid out, so
// columns scrolled into view would sit on their skeleton forever. Observing the
// scroll container itself fixes that, and the root margin gives the frame a head
// start so the cards are usually there by the time the column is on screen.
export default class extends Controller {
  static values = {
    rootSelector: String,
    rootMargin: { type: String, default: "300px" },
    // Load when this document event fires instead of on appearance. Use for a
    // frame inside a hidden panel, where intersection is not a reliable signal.
    loadOnEvent: String,
    loadOnEventDetailId: String,
  }

  connect() {
    if (this.element.hasAttribute("complete")) return
    if (this.element.getAttribute("loading") !== "lazy") return

    if (this.loadOnEventValue) {
      this.handleEvent = (event) => {
        if (this.loadOnEventDetailIdValue && event.detail?.id !== this.loadOnEventDetailIdValue) return
        this.load()
      }
      document.addEventListener(this.loadOnEventValue, this.handleEvent)
      return
    }

    const root = this.rootSelectorValue ? this.element.closest(this.rootSelectorValue) : null

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.load()
      },
      { root, rootMargin: this.rootMarginValue }
    )
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
    this.observer = null
    if (this.handleEvent) {
      document.removeEventListener(this.loadOnEventValue, this.handleEvent)
      this.handleEvent = null
    }
  }

  load() {
    this.disconnect()
    // Turbo loads the frame's src on the loading style changing to eager.
    this.element.setAttribute("loading", "eager")
  }
}
